'use client';
import { useTenant } from '@/contexts/TenantContext';
import { Landscape_Description } from '@/components/ui/catalog/landscape/description';
import { Landscape_Display } from '../landscape/LandscapeDisplay';
import { Landscape_Immersive } from '../landscape/LandscapeImmersive';
import type {
  Immersive,
  LandscapeDisplay,
  LandscapeImmersive,
  LandscapeDescription,
  LandscapeChapterHero,
  LandscapeChapter,
  LandscapeProcess,
  LandscapeSketchbook,
  LandscapeVisionDisplay,
  LandscapeDocumentary,
  LandscapeRenders,
  LandscapePress,
  LandscapeGallery,
  LandscapeExhibition,
  LandscapeEmbed
} from '@/types';
import { Landscape_ChapterHero } from '../landscape/LandscapeChapterHero';
import { Landscape_Chapter } from '../landscape/LandscapeChapter';
import { Landscape_Process } from '../landscape/LandscapeProcess';
import { Landscape_Sketchbook } from '../landscape/LandscapeSketchbook';
import { Common_HeroSection } from '../landscape/CommonHeroSection';
import { EntryInformation, EntryPhotos, readLayout, type EntrySpec } from '../landscape/EntryInformation';
import { Landscape_Documentary } from '../landscape/LandscapeDocumentary';
import { Landscape_Renders } from '../landscape/LandscapeRender';
import { Landscape_Gallery } from '../landscape/LandscapeGallery';
import { Landscape_Press } from '../landscape/LandscapePress';
import { Landscape_Exhibition } from '../landscape/LandscapeExhibition';
import { Landscape_Embed } from '../landscape/LandscapeEmbed';

import { firstMediaData, getMediaData } from '@/lib/utils/media.utils';
import { RoomStage, roomFor } from '../installation/rooms';
import { opensRoom } from '@/components/ui/rooms/resolve';

interface ImmersiveProps {
  detail: Immersive;
}

export default function ImmersiveDetailPage({ detail, startRoom = false }: ImmersiveProps & { startRoom?: boolean }) {
  // Etiketleri dönüştür
  const tags: string[] = [];

  // Etiketleri ekle
  if (detail.tags && Array.isArray(detail.tags)) {
    detail.tags.forEach(tag => {
      if (typeof tag === 'number') {
        tags.push(String(tag));
      } else if (tag && typeof tag === 'object' && 'title' in tag) {
        tags.push(tag.title);
      }
    });
  }

  // Platform bilgisini ekle
  if (detail.platform) {
    const platformWords = detail.platform.split(' ');
    const truncatedPlatform = platformWords.slice(0, 3).join(' ');
    tags.push(truncatedPlatform);
  }

  // An immersive work that is also a room on the site plays the room itself, live, where the
  // still of the artwork would otherwise be, as an installation's page does.
  const { isOrkhan } = useTenant();
  const room = roomFor('immersive', detail, { handBuilt: isOrkhan });
  const cover = room ? firstMediaData(detail.coverImage, detail.cardImage, detail.meta?.image).url || undefined : undefined;
  const stageBlock = room
    ? detail.layout?.find(block => block.blockType === 'LandscapeImmersive') ?? detail.layout?.find(block => block.blockType === 'LandscapeDisplay')
    : undefined;
  const stageStill = stageBlock?.blockType === 'LandscapeImmersive' ? stageBlock.posterImage : stageBlock?.blockType === 'LandscapeDisplay' ? stageBlock.image : undefined;

  // Tarih bilgisini ekle
  if (detail.year) {
    tags.push(detail.year.toString());
  } else if (detail.date) {
    const date = new Date(detail.date);
    const year = date.getFullYear();
    if (year) tags.push(year.toString());
  } else {
    // Tarih yoksa createdAt kullan
    const year = new Date(detail.createdAt).getFullYear();
    if (year) tags.push(year.toString());
  }

  // What the entry knows about itself. Shown by the description block where the layout has
  // one, and by the page itself where it does not, so no entry is left as a bare title.
  const specs: EntrySpec[] = [];

  if (detail.medium) {
    specs.push({
      label: 'Medium',
      specifications: [{ value: detail.medium }],
    });
  }
  if (detail.dimensions) {
    specs.push({
      label: 'Dimensions',
      specifications: [{ value: detail.dimensions }],
    });
  }
  if (detail.platform) {
    specs.push({
      label: 'Platform',
      specifications: [{ value: detail.platform }],
    });
  }
  if (detail.location) {
    specs.push({
      label: 'Location',
      specifications: [{ value: detail.location }],
    });
  }
  if (detail.year) {
    specs.push({
      label: 'Year',
      specifications: [{ value: detail.year.toString() }],
    });
  }
  if (detail.curator) {
    specs.push({
      label: 'Curator',
      specifications: [{ value: detail.curator }],
    });
  }
  if (detail.status && typeof detail.status === 'object' && 'title' in detail.status) {
    specs.push({
      label: 'Status',
      specifications: [{ value: detail.status.title }],
    });
  }
  if (detail.date) {
    const date = new Date(detail.date);
    specs.push({
      label: 'Date',
      // dd/mm/yyyy
      specifications: [{ value: `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`, light: true }],
    });
  }
  if (detail.duration) {
    specs.push({
      label: 'Duration',
      specifications: [{ value: detail.duration }],
    });
  }
  if (detail.experienceUrl) {
    specs.push({
      label: 'URL Experience',
      specifications: [
        {
          value: new URL(detail.experienceUrl).hostname.replace(/^www\./, ''),
          url: detail.experienceUrl,
          light: true,
        },
      ],
    });
  }
  if (detail.requirements) {
    specs.push({
      label: 'Requirements',
      specifications: [{ value: detail.requirements }],
    });
  }
  const page = readLayout(detail.layout);

  const GetLayout = ({
    layout,
  }: {
    layout:
      | LandscapeDisplay
      | LandscapeImmersive
      | LandscapeVisionDisplay
      | LandscapeDescription
      | LandscapeChapterHero
      | LandscapeChapter
      | LandscapeProcess
      | LandscapeSketchbook
      | LandscapeDocumentary
      | LandscapeRenders
      | LandscapeGallery
      | LandscapePress
      | LandscapeExhibition
      | LandscapeEmbed;
  }) => {
    switch (layout.blockType) {
      case 'LandscapeDisplay':
        return <Landscape_Display layout={layout} />;
      case 'LandscapeImmersive':
        return <Landscape_Immersive layout={layout} />;
      case 'LandscapeChapterHero':
        return <Landscape_ChapterHero layout={layout} />;
      case 'LandscapeChapter':
        return <Landscape_Chapter layout={layout} />;
      case 'LandscapeProcess':
        return <Landscape_Process layout={layout} />;
      case 'LandscapeSketchbook':
        return <Landscape_Sketchbook layout={layout} />;
      case 'LandscapeDocumentary':
        return <Landscape_Documentary layout={layout} />;
      case 'LandscapeRenders':
        return <Landscape_Renders layout={layout} />;
      case 'LandscapePress':
        return <Landscape_Press layout={layout} />;
      case 'LandscapeGallery':
        return <Landscape_Gallery layout={layout} />;
      case 'LandscapeExhibition':
        return <Landscape_Exhibition layout={layout} />;
      case 'LandscapeEmbed':
        return <Landscape_Embed layout={layout} />;
      case 'LandscapeDescription':
        // The stage above is the way into the room; a second link to it in the text is left out.
        return <Landscape_Description layout={room && opensRoom(layout.url) ? { ...layout, url: null, urlTitle: null } : layout} specs={specs} />;
      default:
        return null; // Eşleşme yoksa null döndür
    }
  };

  // Content area - ensure overflow works with parent container
  return (
    <div
      className="w-full bg-black relative h-full overflow-y-auto"
      style={{ scrollbarWidth: 'none' }}
      id="landscape-content"
    >
      {/* Hero Section */}
      <Common_HeroSection
        title={detail.title}
        description={detail.description}
        leftTop={detail.platform || detail.location}
        rightTop={detail.duration || (detail.year ? detail.year.toString() : undefined)}
        tags={tags}
      />

      {/* An entry with no description block still shows what it knows. */}
      {/* A work with a room shows the room first, straight under its title. */}
      {room ? <RoomStage room={room} poster={getMediaData(stageStill).url || cover} autoStart={startRoom} /> : null}

      {page.describes ? null : <EntryInformation specs={specs} />}

      {/* Landscape Sections */}
      {detail.layout.map((lay, index) =>
        room && stageBlock && lay === stageBlock ? null : <GetLayout key={index} layout={lay} />,
      )}
      {/* The entry's own photographs, where the page shows no pictures or film of its own. */}
      {page.showsMedia ? null : <EntryPhotos photos={detail.photos} title={detail.title} />}
    </div>
  );
}
